import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useAnimation } from 'framer-motion';
import { ArrowRight, MapPin, Mail, Linkedin, Play } from 'lucide-react';
import SEO from '../components/SEO';

const FounderStory = () => {
  const [heroTextResolved, setHeroTextResolved] = useState(false);
  const [codeTransformed, setCodeTransformed] = useState(false);
  const [countersAnimated, setCountersAnimated] = useState(false);
  const [coastersAnimated, setCoastersAnimated] = useState(false);
  const [signatureAnimated, setSignatureAnimated] = useState(false);

  // Refs for scroll detection
  const problemRef = useRef(null);
  const countersRef = useRef(null);
  const coastersRef = useRef(null);
  const signatureRef = useRef(null);

  const problemInView = useInView(problemRef, { once: true, margin: "-100px" });
  const countersInView = useInView(countersRef, { once: true, margin: "-100px" });
  const coastersInView = useInView(coastersRef, { once: true, margin: "-50px" });
  const signatureInView = useInView(signatureRef, { once: true, margin: "-50px" });

  // Hero scramble effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setHeroTextResolved(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Transform code on scroll
  useEffect(() => {
    if (problemInView && !codeTransformed) {
      const timer = setTimeout(() => setCodeTransformed(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [problemInView, codeTransformed]);

  // Counter animation
  const [counter1, setCounter1] = useState(0);
  const [counter2, setCounter2] = useState(0);

  useEffect(() => {
    if (countersInView && !countersAnimated) {
      setCountersAnimated(true);
      // Animate counter 1 to 30
      let c1 = 0;
      const interval1 = setInterval(() => {
        c1 += 1;
        setCounter1(c1);
        if (c1 >= 30) clearInterval(interval1);
      }, 50);
      // Animate counter 2 to 25
      let c2 = 0;
      const interval2 = setInterval(() => {
        c2 += 1;
        setCounter2(c2);
        if (c2 >= 25) clearInterval(interval2);
      }, 60);
    }
  }, [countersInView, countersAnimated]);

  // Coasters animation
  useEffect(() => {
    if (coastersInView && !coastersAnimated) {
      setCoastersAnimated(true);
    }
  }, [coastersInView, coastersAnimated]);

  // Signature animation
  useEffect(() => {
    if (signatureInView && !signatureAnimated) {
      setSignatureAnimated(true);
    }
  }, [signatureInView, signatureAnimated]);

  // Generate random rotation for coasters
  const getRandomRotation = (index) => {
    const rotations = [-15, 8, -12];
    return rotations[index] || 0;
  };

  // Code lines that transform
  const codeLines = [
    { code: '<span class="text-pink-400">const</span> <span class="text-blue-400">problem</span> <span class="text-orange-400">=</span> <span class="text-green-400">"invisibility"</span>;', human: 'Local businesses are invisible.' },
    { code: '<span class="text-pink-400">if</span> (<span class="text-blue-400">business</span>.<span class="text-yellow-400">quality</span> <span class="text-orange-400">===</span> <span class="text-green-400">"excellent"</span>) {', human: 'Not because they\'re bad.' },
    { code: '&nbsp;&nbsp;<span class="text-blue-400">visibility</span> <span class="text-orange-400">=</span> <span class="text-blue-400">marketingBudget</span> <span class="text-orange-400">?</span> <span class="text-green-400">"high"</span> : <span class="text-green-400">"zero"</span>;', human: 'But because they can\'t outspend chains.' },
    { code: '&nbsp;&nbsp;<span class="text-gray-500">// algorithm favors $$$</span>', human: 'They can\'t game the algorithm.' },
    { code: '&nbsp;&nbsp;<span class="text-blue-400">team</span> <span class="text-orange-400">=</span> <span class="text-pink-400">null</span>;', human: 'They don\'t have marketing departments.' },
    { code: '}', human: '' },
  ];

  return (
    <div className="bg-[#0a0a0f] text-white overflow-x-hidden">
      <SEO
        title="Our Story - How Tappd Started"
        description="Learn how Tappd was born from a simple observation at a bar. From a napkin sketch to a platform connecting local businesses with customers at the perfect moment."
        url="/our-story"
        keywords="Tappd story, founder story, startup story, local advertising origin, Kansas City startup"
      />
      {/* ============================= */}
      {/* HERO - Terminal Style */}
      {/* ============================= */}
      <section className="relative min-h-screen flex items-center py-20 overflow-hidden">
        {/* Animated background grid */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(249,115,22,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,0.1) 1px, transparent 1px)', backgroundSize: '50px 50px' }}></div>
        </div>

        {/* Floating code snippets - scrolling */}
        <div className="absolute top-16 text-orange-400/40 font-mono text-sm hidden lg:block whitespace-nowrap animate-scroll-left">
          &lt;discover local="true" radius="5mi" /&gt;
        </div>
        <div className="absolute top-32 text-amber-400/40 font-mono text-sm hidden lg:block whitespace-nowrap animate-scroll-right">
          function connectNeighbors() {'{ return community; }'}
        </div>
        <div className="absolute top-48 text-orange-400/35 font-mono text-xs hidden lg:block whitespace-nowrap animate-scroll-left" style={{ animationDelay: '-3s' }}>
          const localBusiness = await tappd.discover({'{ type: \'nearby\' }'});
        </div>
        <div className="absolute bottom-48 text-orange-400/40 font-mono text-sm hidden lg:block whitespace-nowrap animate-scroll-left" style={{ animationDelay: '-7s' }}>
          import {'{ Community }'} from '@tappd/local';
        </div>
        <div className="absolute bottom-32 text-amber-400/40 font-mono text-sm hidden lg:block whitespace-nowrap animate-scroll-right" style={{ animationDelay: '-2s' }}>
          export const neighborhood = build(together);
        </div>

        {/* Glowing orbs */}
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl"></div>

        <div className="max-w-[72rem] mx-auto px-6 relative z-10">
          {/* Terminal window */}
          <motion.div
            className="bg-[#1e1b4b]/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Terminal header */}
            <div className="flex items-center gap-2 px-4 py-3 bg-black/30 border-b border-white/10">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="ml-4 text-white/50 text-sm font-mono">founder_story.js</span>
            </div>

            {/* Terminal content */}
            <div className="p-8 md:p-12">
              {/* Initializing line */}
              <div className="font-mono text-white/50 text-sm mb-8 flex items-center gap-2">
                <span className="text-green-400">→</span>
                <span>initializing story...</span>
                <span className="text-green-400">✓</span>
              </div>

              {/* Main headline */}
              <h1 className="text-4xl md:text-6xl lg:text-7xl leading-tight mb-8" style={{ fontFamily: "'Faustina', Georgia, serif", fontWeight: 500 }}>
                <span className="text-white/50 font-mono text-lg block mb-4">// our_story</span>
                <span className="block text-white">Why I Built</span>
                <motion.span
                  className={`block ${heroTextResolved ? 'bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent' : 'text-orange-500 font-mono'}`}
                  style={{ fontFamily: heroTextResolved ? "'Faustina', Georgia, serif" : "'JetBrains Mono', monospace", fontWeight: 500 }}
                >
                  {heroTextResolved ? 'Tappd' : '████████'}
                </motion.span>
              </h1>

              {/* Intro */}
              <div className="max-w-2xl">
                <p className="text-xl md:text-2xl text-white/70 leading-relaxed mb-6">
                  <span className="text-orange-400 font-mono">&gt;</span> Hi, I'm <span className="text-white font-semibold">Rauf</span>.
                </p>
                <p className="text-lg text-white/60 leading-relaxed">
                  I've spent the last decade helping local businesses compete against the big guys.
                  Running a digital marketing agency focused on hospitality taught me something important:
                </p>
                <p className="text-xl text-orange-400 font-medium mt-4">
                  the best businesses aren't always the ones people find
                  <span className="animate-pulse ml-1">█</span>
                </p>
              </div>

              {/* Scroll indicator */}
              <div className="mt-12 flex items-center gap-3 text-white/40">
                <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-1">
                  <motion.div
                    className="w-1.5 h-3 bg-orange-500 rounded-full"
                    animate={{ y: [0, 12, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  />
                </div>
                <span className="text-sm font-mono">scroll to continue</span>
              </div>
            </div>
          </motion.div>

          {/* Founder badge floating */}
          <motion.div
            className="absolute -bottom-6 right-8 md:right-12 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-4 shadow-2xl"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            style={{ boxShadow: '0 0 30px rgba(249, 115, 22, 0.4)' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-2xl">
                👋
              </div>
              <div>
                <div className="font-semibold text-white">Rauf</div>
                <div className="text-white/70 text-sm">Founder & Builder</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============================= */}
      {/* THE PROBLEM */}
      {/* ============================= */}
      <section className="py-24 bg-[#0a0a0f] relative" ref={problemRef}>
        <div className="max-w-[72rem] mx-auto px-6">
          {/* Section header */}
          <motion.div
            className="mb-16"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="font-mono text-orange-500/60 text-sm">/* section: the_problem */</span>
            <h2 className="text-4xl md:text-5xl text-white mt-4 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              The Problem
            </h2>
            <div className="h-1 w-24 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full"></div>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Code that transforms */}
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-[#1e1b4b] rounded-xl p-6 font-mono text-sm leading-loose">
                {codeLines.map((line, index) => (
                  <div
                    key={index}
                    className={`transition-all duration-500 ${codeTransformed && line.human ? 'text-white text-lg py-1' : ''}`}
                    style={{ fontFamily: codeTransformed && line.human ? "'DM Sans', sans-serif" : "'JetBrains Mono', monospace" }}
                  >
                    {codeTransformed && line.human ? (
                      line.human
                    ) : (
                      <span dangerouslySetInnerHTML={{ __html: line.code }} />
                    )}
                  </div>
                ))}
              </div>

              <button
                onClick={() => setCodeTransformed(true)}
                className="mt-6 px-6 py-3 bg-orange-500/20 border border-orange-500/50 rounded-full text-orange-400 font-mono text-sm hover:bg-orange-500/30 transition-all flex items-center gap-2"
              >
                <Play className="w-4 h-4" />
                run translation.js
              </button>
            </motion.div>

            {/* Right: Impact stats */}
            <motion.div
              className="space-y-6"
              ref={countersRef}
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                    <span className="text-2xl">📉</span>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-400">{counter1}%</div>
                    <div className="text-white/50 text-sm">lost to delivery apps</div>
                  </div>
                </div>
                <p className="text-white/60 text-sm">I watched restaurants hemorrhage revenue to middlemen who don't care about their food.</p>
              </div>

              <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                    <span className="text-2xl">🏨</span>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-400">{counter2}%</div>
                    <div className="text-white/50 text-sm">commission to OTAs</div>
                  </div>
                </div>
                <p className="text-white/60 text-sm">Boutique hotels paying a quarter of each booking to faceless platforms.</p>
              </div>

              <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                    <span className="text-2xl">💸</span>
                  </div>
                  <div>
                    <div className="text-white/50 text-sm">Dozens of businesses</div>
                    <div className="text-lg font-semibold text-white">Fighting for scraps online</div>
                  </div>
                </div>
                <p className="text-white/60 text-sm">I helped them all. But I couldn't fix the system. The system is broken.</p>
              </div>
            </motion.div>
          </div>

          {/* Pull quote */}
          <motion.div
            className="mt-16"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 rounded-2xl p-8 border border-orange-500/20">
              <p className="text-2xl md:text-3xl text-white text-center" style={{ fontFamily: "'Playfair Display', serif" }}>
                "Discovery shouldn't require a <span className="text-orange-400">marketing budget</span>."
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============================= */}
      {/* THE TRIGGER */}
      {/* ============================= */}
      <section className="py-24 bg-gradient-to-b from-[#0a0a0f] to-[#1e1b4b] relative overflow-hidden">
        {/* Ambient bar scene overlay */}
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=1920"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1e1b4b] via-[#1e1b4b]/80 to-[#0a0a0f]"></div>
        </div>

        <div className="max-w-[72rem] mx-auto px-6 relative z-10">
          {/* Section header */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="font-mono text-orange-500/60 text-sm">/* section: the_trigger */</span>
            <h2 className="text-4xl md:text-5xl text-white mt-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              The Moment Everything Changed
            </h2>
          </motion.div>

          {/* Cinematic story */}
          <div className="space-y-8">
            {[
              { text: <>One night, I was sitting at my local bar in <span className="text-orange-400">Medford, New Jersey</span>.</> },
              { text: <>I'd noticed something over the years: most local bars didn't have a consistent supply of coasters. Customers used <span className="text-white/50 italic">napkins instead</span> — which get soggy and frustrating almost instantly, especially in the summer heat.</> },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="flex gap-6 items-start"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <div className="w-2 h-2 bg-orange-500 rounded-full mt-3 flex-shrink-0" style={{ boxShadow: '0 0 10px rgba(249, 115, 22, 0.5)' }}></div>
                <p className="text-xl text-white/80 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>

          {/* Dirty napkin evidence */}
          <motion.div
            className="my-12 ml-8"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex flex-col sm:flex-row gap-6 items-center justify-center">
              <motion.div
                initial={{ opacity: 0, x: -60 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <img src="https://iolab.co/wp-content/uploads/2025/12/dirty-napkin1.png" alt="Soggy napkin" className="w-48 h-48 md:w-60 md:h-60 object-contain drop-shadow-2xl transform -rotate-6"/>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 60 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <img src="https://iolab.co/wp-content/uploads/2025/12/dirty-napkin2.png" alt="Wet napkin" className="w-48 h-48 md:w-60 md:h-60 object-contain drop-shadow-2xl transform rotate-4"/>
              </motion.div>
            </div>
            <p className="text-center text-white/40 text-sm mt-4 font-mono">// sound familiar?</p>
          </motion.div>

          {/* More story */}
          <div className="space-y-8">
            {[
              { text: <>When bars did have coasters, they were free handouts from distributors — <span className="text-white">generic, inconsistent, delivered on someone else's schedule</span>. A Bud Light logo nobody asked for.</> },
              { text: <>That night, I looked down at the coaster under my drink — a generic beer logo, <span className="text-white/50 italic">serving no real purpose</span> — and thought:</> },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="flex gap-6 items-start"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <div className="w-2 h-2 bg-orange-500 rounded-full mt-3 flex-shrink-0"></div>
                <p className="text-xl text-white/80 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>

          {/* The big question */}
          <motion.div
            className="my-16"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-white/10 text-center">
              <p className="font-mono text-orange-500 text-sm mb-4">&gt; query.execute()</p>
              <p className="text-3xl md:text-5xl text-white leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                "What if this could <span className="bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">do something</span>?"
              </p>
            </div>
          </motion.div>

          {/* Realization */}
          <motion.div
            className="space-y-6 text-lg text-white/70 leading-relaxed max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p>
              People are already out. They're relaxed. They're open to discovery. And they're surrounded by other local businesses they've never heard of.
            </p>
            <p className="text-white text-xl font-medium">
              What if that coaster could connect them?
            </p>
            <p>
              Not with ads for more food and drinks — but with the gym down the street, the salon around the corner, the boutique they'd love but never noticed.
            </p>
          </motion.div>

          {/* Napkins tagline */}
          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-2xl md:text-3xl text-white/80 italic" style={{ fontFamily: "'Playfair Display', serif" }}>
              "Napkins are for ideas..."
            </p>
          </motion.div>

          {/* Coaster prototype */}
          <motion.div
            className="mt-12 flex justify-center"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="relative">
              <motion.img
                src="https://tappd-uploads.nyc3.cdn.digitaloceanspaces.com/coaster_designs/tappd-coaster-1c-value.png"
                alt="First Tappd coaster prototype"
                className="w-72 h-72 md:w-96 md:h-96 object-contain"
                animate={{ y: [0, -20, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              />
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-black/80 backdrop-blur px-4 py-2 rounded-full">
                <span className="font-mono text-orange-400 text-sm">v1.prototype</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============================= */}
      {/* THE SOLUTION */}
      {/* ============================= */}
      <section className="py-24 bg-[#1e1b4b] relative overflow-hidden">
        <div className="max-w-[72rem] mx-auto px-6">
          {/* Section header */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="font-mono text-orange-500/60 text-sm">/* section: the_solution */</span>
            <h2 className="text-4xl md:text-5xl text-white mt-4 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              So I Built It
            </h2>
            <div className="h-1 w-24 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full mx-auto"></div>
          </motion.div>

          {/* Solution statement */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-2xl md:text-3xl text-orange-400 font-semibold max-w-3xl mx-auto">
              Tappd turns bar coasters into a hyperlocal discovery network.
            </p>
          </motion.div>

          {/* How it works */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-black/30 rounded-3xl p-8 md:p-12 border border-white/10">
              <div className="grid md:grid-cols-3 gap-8 text-center">
                {[
                  { icon: '📱', title: 'Scan', desc: 'QR code on your coaster. No app needed.' },
                  { icon: '🎯', title: 'Discover', desc: 'Deals from local businesses nearby.' },
                  { icon: '💰', title: 'Save', desc: 'Stop on your way home. Support local.' },
                ].map((step, index) => (
                  <motion.div
                    key={index}
                    className="space-y-4"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                  >
                    <div className="w-20 h-20 mx-auto bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center text-4xl shadow-lg" style={{ boxShadow: '0 10px 30px rgba(249, 115, 22, 0.3)' }}>
                      {step.icon}
                    </div>
                    <h3 className="font-semibold text-white text-lg">{step.title}</h3>
                    <p className="text-white/60 text-sm">{step.desc}</p>
                  </motion.div>
                ))}
              </div>

              {/* Code comment */}
              <div className="mt-12 font-mono text-sm text-center">
                <span className="text-white/50">// </span>
                <span className="text-pink-400">no</span>
                <span className="text-white/50"> app download. </span>
                <span className="text-pink-400">no</span>
                <span className="text-white/50"> algorithm. </span>
                <span className="text-green-400">just</span>
                <span className="text-white/50"> real offers from real neighbors.</span>
              </div>
            </div>
          </motion.div>

          {/* Three beneficiaries */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              { icon: '🏪', title: 'For Local Businesses', desc: 'Advertising that makes sense — reaching people already out, already nearby, already ready to explore.' },
              { icon: '🍺', title: 'For Bars & Restaurants', desc: 'Free coasters that make customers happy and position you as a community hub.' },
              { icon: '👋', title: 'For Everyone', desc: 'Save money while supporting the places that make your neighborhood worth living in.' },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-white/5 rounded-2xl p-6 border border-white/10 hover:border-orange-500/50 transition-all group"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">{item.icon}</div>
                <h3 className="font-semibold text-white text-lg mb-2">{item.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================= */}
      {/* MY COMMITMENT */}
      {/* ============================= */}
      <section className="py-24 bg-gradient-to-b from-[#1e1b4b] to-[#FEF7ED]">
        <div className="max-w-[72rem] mx-auto px-6">
          {/* Section header */}
          <motion.div
            className="mb-12"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="font-mono text-orange-500/60 text-sm">/* section: my_commitment */</span>
            <h2 className="text-4xl md:text-5xl text-white mt-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              This Is Personal
            </h2>
          </motion.div>

          <div className="space-y-6">
            {[
              { text: "I'm not building Tappd to flip it.", className: "text-xl text-white font-medium" },
              { text: "I'm building it because I believe local businesses deserve a fair shot.", className: "text-lg text-white/70 leading-relaxed" },
              { text: <>I'm building this in South Jersey because <span className="text-white">it's home</span> — where my daughter is growing up, where I know the shop owners by name. This isn't some abstract "local business" problem to me. <span className="text-orange-400">These are my neighbors.</span></>, className: "text-lg text-white/70 leading-relaxed" },
              { text: "I'm personally involved in every coaster placement, every business partnership, and every line of code.", className: "text-lg text-white/70 leading-relaxed" },
            ].map((item, index) => (
              <motion.p
                key={index}
                className={item.className}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                {item.text}
              </motion.p>
            ))}

            <motion.div
              className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-white text-lg font-medium">
                When you email support, you're probably emailing me.
              </p>
            </motion.div>

            <motion.p
              className="text-lg text-white/70 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              This is just the beginning. We're starting in South Jersey, proving the model works, and growing from there — <span className="text-white">one neighborhood at a time</span>.
            </motion.p>
          </div>
        </div>
      </section>

      {/* ============================= */}
      {/* SIGNATURE & FIRST PARTNER */}
      {/* ============================= */}
      <section className="py-24 bg-[#FEF7ED]">
        <div className="max-w-[72rem] mx-auto px-6">
          {/* Pull quote */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block text-left border-l-4 border-orange-500 pl-6">
              <p className="text-2xl md:text-3xl text-[#1e1b4b] italic" style={{ fontFamily: "'Playfair Display', serif" }}>
                "If you believe local matters,<br/>I'd love for you to be part of this."
              </p>
            </div>
          </motion.div>

          {/* Coasters with badges */}
          <div className="relative py-8 mb-12" ref={coastersRef}>
            <div className="relative h-[420px] md:h-[520px] flex justify-center items-center">
              {/* Coaster images - 75% larger (order: 2, 3, 1) */}
              {[
                { src: 'https://tappd-uploads.nyc3.cdn.digitaloceanspaces.com/coaster_designs/tappd-coaster-8e-community.png', left: '0%', delay: 0 },
                { src: 'https://tappd-uploads.nyc3.cdn.digitaloceanspaces.com/coaster_designs/tappd-coaster-8a-community.png', left: '28%', delay: 0.2 },
                { src: 'https://tappd-uploads.nyc3.cdn.digitaloceanspaces.com/coaster_designs/tappd-coaster-3b-context.png', left: '56%', delay: 0.4 },
              ].map((coaster, index) => (
                <motion.div
                  key={index}
                  className="absolute w-[280px] h-[280px] md:w-[392px] md:h-[392px]"
                  style={{ left: coaster.left, zIndex: index + 1 }}
                  initial={{ opacity: 0, y: 60, scale: 0.9 }}
                  animate={coastersAnimated ? {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    rotate: getRandomRotation(index)
                  } : {}}
                  transition={{ duration: 0.8, delay: coaster.delay, ease: [0.34, 1.56, 0.64, 1] }}
                >
                  <img src={coaster.src} alt="Tappd Coaster" className="w-full h-full object-contain drop-shadow-2xl"/>
                </motion.div>
              ))}

              {/* Floating badges */}
              <motion.a
                href="https://www.linkedin.com/in/rauf-tur/"
                target="_blank"
                rel="noopener noreferrer"
                className="absolute top-0 left-[5%] bg-white rounded-full px-4 py-2 shadow-lg border border-[#1e1b4b]/10 hover:bg-[#1e1b4b] hover:text-white transition-colors flex items-center gap-2 z-20 text-[#1e1b4b]"
                initial={{ opacity: 0, y: 15 }}
                animate={coastersAnimated ? { opacity: 1, y: [0, -8, 0] } : {}}
                transition={{ opacity: { duration: 0.5, delay: 0.9 }, y: { duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1.5 } }}
              >
                <Linkedin className="w-4 h-4" />
                <span className="text-sm font-medium">LinkedIn</span>
              </motion.a>

              <motion.a
                href="mailto:rauf@tappd.app"
                className="absolute bottom-[10%] right-[8%] bg-[#1e1b4b] rounded-full px-4 py-2 shadow-lg flex items-center gap-2 z-20 hover:bg-orange-500 transition-colors"
                initial={{ opacity: 0, y: 15 }}
                animate={coastersAnimated ? { opacity: 1, y: [0, -8, 0] } : {}}
                transition={{ opacity: { duration: 0.5, delay: 1.3 }, y: { duration: 3, repeat: Infinity, ease: "easeInOut", delay: 2.1 } }}
              >
                <Mail className="w-4 h-4 text-white" />
                <span className="text-sm font-medium text-white">rauf@tappd.app</span>
              </motion.a>
            </div>

            {/* Medford Lakes badge - below middle coaster */}
            <motion.div
              className="flex justify-center mt-4"
              initial={{ opacity: 0, y: 15 }}
              animate={coastersAnimated ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 1.1 }}
            >
              <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-full px-4 py-2 shadow-lg flex items-center gap-2">
                <MapPin className="w-4 h-4 text-white" />
                <span className="text-sm font-semibold text-white">Medford Lakes, NJ</span>
              </div>
            </motion.div>
          </div>

          {/* Handwritten signature */}
          <div className="text-center mb-20" ref={signatureRef}>
            <motion.div
              className="inline-block"
              initial={{ opacity: 0 }}
              animate={signatureAnimated ? { opacity: 1 } : {}}
              transition={{ duration: 0.5 }}
            >
              <motion.img
                src="https://customer-assets.emergentagent.com/job_tappd-journey/artifacts/n8wp8jgy_Untitled-1.png"
                alt="Rauf's Signature"
                className="h-20 md:h-28 w-auto mx-auto"
                initial={{ opacity: 0 }}
                animate={signatureAnimated ? { opacity: 1 } : {}}
                transition={{ duration: 1, ease: "easeOut" }}
              />
              <motion.p
                className="text-[#1e1b4b]/50 text-sm font-medium tracking-wider uppercase mt-2"
                initial={{ opacity: 0, y: 10 }}
                animate={signatureAnimated ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 1.8 }}
              >
                Founder, Tappd
              </motion.p>
            </motion.div>
          </div>

          {/* First partner */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-orange-100 overflow-hidden relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-100 to-amber-100 rounded-full -translate-y-1/2 translate-x-1/2"></div>

              <div className="relative">
                <span className="font-mono text-orange-500 text-sm">partners[0]</span>
                <h3 className="text-2xl text-[#1e1b4b] mt-2 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Our First Partner</h3>

                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 bg-[#1e1b4b] rounded-2xl flex items-center justify-center flex-shrink-0">
                    <span className="text-4xl">🍺</span>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-[#1e1b4b]">Ott's Tavern</h4>
                    <p className="text-[#1e1b4b]/60 text-sm">Medford, NJ</p>
                    <p className="text-[#1e1b4b]/70 mt-2">
                      The first bar to believe in this idea. Where the first Tappd coasters will hit tables.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============================= */}
      {/* CTA SECTION */}
      {/* ============================= */}
      <section className="py-24 bg-[#1e1b4b] relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-[72rem] mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="font-mono text-orange-500/60 text-sm">/* section: join_us */</span>
            <h2 className="text-4xl md:text-5xl text-white mt-4 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Want to bring Tappd to <span className="text-orange-400">your neighborhood?</span>
            </h2>
            <p className="text-white/60 text-lg mb-12 max-w-xl mx-auto">
              Whether you're a local business, a bar owner, or just curious — I'd love to hear from you.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/for-businesses"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full text-lg font-semibold text-white shadow-xl hover:scale-105 transition-all"
                style={{ boxShadow: '0 10px 30px rgba(249, 115, 22, 0.3)' }}
              >
                I'm a Local Business
                <ArrowRight className="w-5 h-5" />
              </Link>

              <Link
                to="/for-venues"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 border-2 border-white/20 rounded-full text-lg font-semibold text-white hover:bg-white/20 hover:scale-105 transition-all"
              >
                I'm a Bar/Restaurant
              </Link>

              <Link
                to="/how-it-works"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white/20 rounded-full text-lg font-semibold text-white/80 hover:bg-white/10 hover:scale-105 transition-all"
              >
                I'm Just Curious
              </Link>
            </div>

            {/* Terminal-style email */}
            <div className="mt-12 font-mono text-sm">
              <span className="text-white/40">&gt; contact.direct(</span>
              <a href="mailto:rauf@tappd.app" className="text-orange-400 hover:underline">"rauf@tappd.app"</a>
              <span className="text-white/40">)</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Custom CSS for scrolling animations */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&family=JetBrains+Mono:wght@400;500&display=swap');

        @keyframes scroll-left {
          0% { transform: translateX(100vw); }
          100% { transform: translateX(-100%); }
        }
        @keyframes scroll-right {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100vw); }
        }
        .animate-scroll-left {
          animation: scroll-left 25s linear infinite;
        }
        .animate-scroll-right {
          animation: scroll-right 30s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default FounderStory;
